/*
 * File: AttendanceStatus.tsx
 * Author: Austin Robinson
 * Date Created: 12/12/2025
 * Copyright (c) DrivingDoc 2025
 */
import { useMemo } from 'react';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import Alert, { AlertColor } from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';

import { MinimalUser } from 'api';
import { AttendanceValue, getAttendanceLabel } from 'utils/getAttendanceLabel';

// Configure dayjs calendar plugin once
dayjs.extend(calendar);

/**
 * Configuration for calendar date formatting
 */
const CALENDAR_FORMAT = {
  sameElse: 'MMMM D, YYYY [at] h:mm A',
} as const;

/**
 * Props for AttendanceStatus component
 */
export interface AttendanceStatusProps {
  /**
   * Attendance value ('present', 'absent', 'no_permit', etc.)
   */
  attendance: AttendanceValue;

  /**
   * ISO string of when the attendance was taken
   */
  attendance_taken_at?: string | null;

  /**
   * Information about the user who took attendance
   */
  attendance_taken_by_user?: MinimalUser | null;
}

/**
 * Alert configuration based on attendance value
 */
interface AlertConfig {
  severity: AlertColor;
  icon: JSX.Element;
}

/**
 * Gets the appropriate alert configuration for the given attendance value
 */
function getAlertConfig(attendance: AttendanceValue): AlertConfig {
  return attendance === 'present'
    ? { severity: 'success', icon: <CheckCircleIcon /> }
    : { severity: 'warning', icon: <ErrorIcon /> };
}

/**
 * Builds a display name from user information
 * Prefers nickname over first name
 */
function buildDisplayName(user: MinimalUser | null | undefined): string {
  if (!user) return '';

  const { nickname, first_name, last_name } = user;
  const firstName = nickname ?? first_name ?? '';
  const fullName = `${firstName} ${last_name ?? ''}`.trim();

  return fullName;
}

/**
 * Formats a timestamp using dayjs calendar format
 */
function formatTimestamp(timestamp: string | null | undefined): string {
  if (!timestamp) return '';
  return dayjs(timestamp).calendar(null, CALENDAR_FORMAT);
}

/**
 * Builds the complete attendance status message
 */
function buildStatusMessage(
  attendance: AttendanceValue,
  displayName: string,
  formattedTime: string,
): string {
  const parts: string[] = [`Marked as ${getAttendanceLabel(attendance)}`];

  if (displayName) parts.push(`by ${displayName}`);
  if (formattedTime) parts.push(formattedTime);

  return parts.join(' ');
}

/**
 * Displays attendance status information in a readable, human-friendly format.
 * Shows who marked the attendance, the attendance value, and when it was taken.
 *
 * @param props - AttendanceStatusProps
 * @returns JSX.Element displaying the attendance status
 */
export default function AttendanceStatus({
  attendance,
  attendance_taken_at,
  attendance_taken_by_user,
}: AttendanceStatusProps) {
  // Memoize computed values to avoid unnecessary recalculations
  const displayName = useMemo(() => buildDisplayName(attendance_taken_by_user), [attendance_taken_by_user]);

  const formattedTime = useMemo(() => formatTimestamp(attendance_taken_at), [attendance_taken_at]);

  const message = useMemo(
    () => buildStatusMessage(attendance, displayName, formattedTime),
    [attendance, displayName, formattedTime],
  );

  const alertConfig = useMemo(() => getAlertConfig(attendance), [attendance]);

  // Handle null attendance (not yet marked)
  if (attendance === null) {
    return (
      <Alert severity="info" data-testid="attendance-status">
        <Typography variant="body2">Attendance not yet marked</Typography>
      </Alert>
    );
  }

  return (
    <Alert severity={alertConfig.severity} icon={alertConfig.icon} data-testid="attendance-status">
      <Typography variant="body2" data-testid="attendance-status-text">
        {message}
      </Typography>
    </Alert>
  );
}